import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import {
  border,
  borderColor,
  borderStyle,
  borderWidth,
  display,
  height,
  width as styledWidth,
  type WidthProps,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import { ResponsiveLazyImage } from 'common/components/LazyImage';
import type { ImageSizesWithAlt } from 'common/components/LazyImage/types';
import { Link } from 'common/components/Link';
import { useIsObserved } from 'common/hooks/intersection-observer';
import { borderRadiusBL, borderRadiusBR } from 'common/theme/system-utilities';
import type { CardTextBlockProps } from './CardTextBlock';
import { CardTextBlock } from './CardTextBlock';
import { useTrackingEvents } from './hooks';

interface CardProps extends CardTextBlockProps, WidthProps {
  image: ImageSizesWithAlt;
  href: string;
  index: number;
}

const UnstyledCardLink = styled(Link)<any>`
  ${display};
  ${height};
  ${styledWidth};
`;

const CardLink = ({ width = 1, ...rest }: ComponentProps<typeof UnstyledCardLink>) => (
  <UnstyledCardLink display="flex" height="100%" width={width} {...rest} />
);

const UnstyledCardPicture = styled(ResponsiveLazyImage)<any>`
  ${styledWidth};
`;

const CardPicture = (props: ComponentProps<typeof UnstyledCardPicture>) => (
  <UnstyledCardPicture width={1} {...props} />
);

const UnstyledCardContent = styled(Flex)<any>`
  ${borderRadiusBL};
  ${borderRadiusBR};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border};
  ${styledWidth};
`;

const CardContent = (props: ComponentProps<typeof UnstyledCardContent>) => (
  <UnstyledCardContent
    as="article"
    flexDirection="column"
    borderRadiusBL="small"
    borderRadiusBR="small"
    width={1}
    borderStyle="solid"
    borderWidth="small"
    borderColor="sys.neutral.border.inactive"
    {...props}
  />
);

export const Card = ({ href, subline, headline, image, index, width }: CardProps) => {
  const [ref, isObserved] = useIsObserved();
  const { onImageView, onImageClick } = useTrackingEvents(index, headline);
  const label = headline || subline;

  useEffect(() => {
    if (isObserved) {
      onImageView();
    }
  }, [isObserved]);

  return (
    <CardLink ref={ref} href={href} aria-label={label} onClick={onImageClick} width={width}>
      <CardContent>
        <CardPicture {...image} />
        {label && <CardTextBlock headline={headline} subline={subline} />}
      </CardContent>
    </CardLink>
  );
};
